import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Get stored users from localStorage
  const getStoredUsers = () => {
    const users = localStorage.getItem('users');
    return users ? JSON.parse(users) : [];
  };

  // Save users to localStorage
  const saveUsers = (users) => {
    localStorage.setItem('users', JSON.stringify(users));
  };

  const verifyToken = useCallback(async (token) => {
    try {
      // Get user data from token (in a real app, you'd verify the JWT)
      const userData = localStorage.getItem('currentUser');
      
      if (userData) {
        const currentUser = JSON.parse(userData);
        setUser(currentUser);
        setIsAuthenticated(true);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if user is already logged in on app start
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Set default headers for all requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Verify token with backend (you'll implement this)
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      
      // Get stored users
      const users = getStoredUsers();
      
      // Find user by email
      const user = users.find(u => u.email === email);
      
      if (!user) {
        setLoading(false);
        return { success: false, error: 'User not found. Please sign up first.' };
      }
      
      // Check password (in a real app, you'd hash and compare passwords)
      if (user.password !== password) {
        setLoading(false);
        return { success: false, error: 'Invalid password.' };
      }
      
      // Create token and save user session
      const token = 'auth-token-' + Date.now();
      localStorage.setItem('authToken', token);
      localStorage.setItem('currentUser', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(user);
      setIsAuthenticated(true);
      setLoading(false);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      setLoading(false);
      return { success: false, error: error.response?.data?.message || 'Login failed' };
    }
  };

  const signup = async (name, email, password) => {
    try {
      setLoading(true);
      
      // Get stored users
      const users = getStoredUsers();
      
      // Check if user already exists
      const existingUser = users.find(u => u.email === email);
      if (existingUser) {
        setLoading(false);
        return { success: false, error: 'User with this email already exists.' };
      }
      
      // Create new user
      const newUser = {
        id: Date.now().toString(),
        name,
        email,
        password, // In a real app, you'd hash this password
        avatar: null,
        createdAt: new Date().toISOString()
      };
      
      // Add user to storage
      users.push(newUser);
      saveUsers(users);
      
      // Create token and save user session
      const token = 'auth-token-' + Date.now();
      localStorage.setItem('authToken', token);
      localStorage.setItem('currentUser', JSON.stringify(newUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(newUser);
      setIsAuthenticated(true);
      setLoading(false);
      
      return { success: true };
    } catch (error) {
      console.error('Signup failed:', error);
      setLoading(false);
      return { success: false, error: error.response?.data?.message || 'Signup failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (userData) => {
    try {
      // Update user in storage
      const users = getStoredUsers();
      const updatedUsers = users.map(u => 
        u.id === user.id ? { ...u, ...userData } : u
      );
      saveUsers(updatedUsers);
      
      // Update current user
      const updatedUser = { ...user, ...userData };
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true };
    } catch (error) {
      console.error('Profile update failed:', error);
      return { success: false, error: error.response?.data?.message || 'Profile update failed' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      // Verify current password
      if (user.password !== currentPassword) {
        return { success: false, error: 'Current password is incorrect' };
      }

      // Update password in storage
      const users = getStoredUsers();
      const updatedUsers = users.map(u => 
        u.id === user.id ? { ...u, password: newPassword } : u
      );
      saveUsers(updatedUsers);
      
      // Update current user
      const updatedUser = { ...user, password: newPassword };
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true };
    } catch (error) {
      console.error('Password change failed:', error);
      return { success: false, error: 'Password change failed' };
    }
  };

  const updateNotificationSettings = async (settings) => {
    try {
      const updatedUser = { 
        ...user, 
        notificationSettings: { ...user.notificationSettings, ...settings } 
      };
      
      // Update in storage
      const users = getStoredUsers();
      const updatedUsers = users.map(u => 
        u.id === user.id ? updatedUser : u
      );
      saveUsers(updatedUsers);
      
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true };
    } catch (error) {
      console.error('Notification settings update failed:', error);
      return { success: false, error: 'Settings update failed' };
    }
  };

  const updateAppearanceSettings = async (settings) => {
    try {
      const updatedUser = { 
        ...user, 
        appearanceSettings: { ...user.appearanceSettings, ...settings } 
      };
      
      // Update in storage
      const users = getStoredUsers();
      const updatedUsers = users.map(u => 
        u.id === user.id ? updatedUser : u
      );
      saveUsers(updatedUsers);
      
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true };
    } catch (error) {
      console.error('Appearance settings update failed:', error);
      return { success: false, error: 'Settings update failed' };
    }
  };

  const exportUserData = () => {
    try {
      const userData = {
        profile: {
          name: user.name,
          email: user.email,
          bio: user.bio,
          website: user.website,
          location: user.location,
          avatar: user.avatar
        },
        settings: {
          notifications: user.notificationSettings,
          appearance: user.appearanceSettings
        },
        createdAt: user.createdAt
      };
      
      const blob = new Blob([JSON.stringify(userData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${user.name}-profile-data.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Export failed:', error);
      return { success: false, error: 'Export failed' };
    }
  };

  const deleteAccount = async (password) => {
    try {
      // Verify password
      if (user.password !== password) {
        return { success: false, error: 'Password is incorrect' };
      }

      // Remove user from storage
      const users = getStoredUsers();
      const updatedUsers = users.filter(u => u.id !== user.id);
      saveUsers(updatedUsers);
      
      // Logout user
      logout();
      
      return { success: true };
    } catch (error) {
      console.error('Account deletion failed:', error);
      return { success: false, error: 'Account deletion failed' };
    }
  };

  // Function to clear all stored users (for testing)
  const clearAllUsers = () => {
    localStorage.removeItem('users');
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    signup,
    logout,
    updateProfile,
    changePassword,
    updateNotificationSettings,
    updateAppearanceSettings,
    exportUserData,
    deleteAccount,
    clearAllUsers
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
